import React, { useEffect, useMemo, useState } from 'react';
import { getDefaultDates } from '../utils/dateUtils';
import { defaultSelectStyles as selectStyles } from '../utils/selectStyles';
import { canAccess } from '../utils/canAccess';
import axios from 'axios';
import { Card, Row, Col, Form, Button, ButtonGroup, Alert, Badge, Spinner, Modal, Table } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import Select from 'react-select';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  FaCalendarAlt,
  FaMoneyBillWave,
  FaHardHat,
  FaBuilding,
  FaIdCard,
  FaGraduationCap,
  FaUniversity,
  FaClock,
  FaFileExport,
  FaBroom,
  FaUsers,
  FaCoins,
  FaListAlt,
  FaFilter,
  FaExclamationTriangle,
  FaWrench,
  FaLock,
  FaClone,
  FaTrashAlt
} from 'react-icons/fa';
import KpiCard from '../components/KpiCard';

const dh =(value) => `${Number(value || 0).toLocaleString('fr-FR')} DH`;

const sanitizeFileName = (value) => {
  return String(value || 'Sans_Chantier')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const excelHeaders = (mode) => ([
  'ID_Ouvrier', 'Ouvrier', 'CIN', 'Taux', 'Qualification', 'Matricule_Sage', 'Societe', 'RIB',
  mode === 'doublant' ? 'Chantier' : 'Dernier_Chantier',
  'Jours', 'Heures', 'Paie', 'Nb_Pointages', 'Taux_Min', 'Taux_Max', 'Anomalie_Taux'
]);

const buildExcelRows = (data, mode) => {
  const chantierLabel = mode === 'doublant' ? 'Chantier' : 'Dernier_Chantier';

  return data.map((row) => ({
    ID_Ouvrier: row.id_ouvrier,
    Ouvrier: row.ouvrier,
    CIN: row.cin,
    Taux: row.taux,
    Qualification: row.qualification,
    Matricule_Sage: row.matricule_sage,
    Societe: row.societe,
    RIB: row.rib,
    [chantierLabel]: row.dernier_chantier,
    Jours: row.jours,
    Heures: row.heures,
    Paie: row.paie,
    Nb_Pointages: row.nb_pointages,
    Taux_Min: row.taux_min,
    Taux_Max: row.taux_max,
    Anomalie_Taux: Number(row.taux_min || 0) !== Number(row.taux_max || 0) ? 'OUI' : 'NON'
  }));
};

const buildExcelSheet = (data, mode) => {
  const ws = XLSX.utils.json_to_sheet(buildExcelRows(data, mode), { header: excelHeaders(mode) });
  const ref = ws['!ref'];

  if (ref) {
    const range = XLSX.utils.decode_range(ref);
    for (let col = range.s.c; col <= range.e.c; col++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[addr]) continue;
      ws[addr].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: '1F3864' } }
      };
    }
    ws['!autofilter'] = { ref };
  }

  return ws;
};

const PaieMoisPage = ({ user }) => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();
  const canFixAnomalies = canAccess(user, 'paie-mois:write');

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingParChantier, setExportingParChantier] = useState(false);
  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('final');
  const [selectedChantiers, setSelectedChantiers] = useState([]);
  const [selectedOuvriers, setSelectedOuvriers] = useState([]);
  const [kpiFilter, setKpiFilter] = useState('ALL');

  const [showAnomalyModal, setShowAnomalyModal] = useState(false);
  const [fixingAnomalies, setFixingAnomalies] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const [showDoublonsModal, setShowDoublonsModal] = useState(false);
  const [loadingDoublons, setLoadingDoublons] = useState(false);
  const [doublonsPreview, setDoublonsPreview] = useState(null);
  const [cleaningDoublons, setCleaningDoublons] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const [resume, setResume] = useState({
    totalOuvriers: 0,
    totalPaie: 0,
    totalJours: 0,
    totalHeures: 0
  });

  const showAlert = (type, text) => {
    setAlertMsg({ type, text });
    if (text) {
      setTimeout(() => {
        setAlertMsg({ type: '', text: '' });
      }, 3500);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`/rapport/paie-mois?start=${startDate}&end=${endDate}&mode=${mode}`);
      const payload = res.data || {};

      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setResume({
        totalOuvriers: Number(payload.totalOuvriers || 0),
        totalPaie: Number(payload.totalPaie || 0),
        totalJours: Number(payload.totalJours || 0),
        totalHeures: Number(payload.totalHeures || 0)
      });
    } catch (err) {
      console.error('❌ Erreur chargement paie mois :', err);
      setRows([]);
      setResume({
        totalOuvriers: 0,
        totalPaie: 0,
        totalJours: 0,
        totalHeures: 0
      });
      showAlert('danger', err?.response?.data?.message || '❌ Erreur lors du chargement de la paie');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, mode]);

  const chantierOptions = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      if (row?.dernier_chantier) {
        map.set(row.dernier_chantier, {
          value: row.dernier_chantier,
          label: row.dernier_chantier
        });
      }
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [rows]);

  const ouvrierOptions = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      if (!map.has(row.id_ouvrier)) {
        map.set(row.id_ouvrier, {
          value: row.id_ouvrier,
          label: `${row.ouvrier || 'Sans nom'}${row.cin ? ` - ${row.cin}` : ''}`
        });
      }
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [rows]);

  const resetAllFilters = () => {
    setKpiFilter('ALL');
    setSelectedChantiers([]);
    setSelectedOuvriers([]);
  };

  const filterByChantierOuvrier = (data) => {
    let result = [...data];

    if (selectedChantiers.length > 0) {
      const chantierSet = new Set(selectedChantiers.map((x) => x.value));
      result = result.filter((row) => chantierSet.has(row.dernier_chantier));
    }

    if (selectedOuvriers.length > 0) {
      const ouvrierSet = new Set(selectedOuvriers.map((x) => Number(x.value)));
      result = result.filter((row) => ouvrierSet.has(Number(row.id_ouvrier)));
    }

    return result;
  };

  const baseFilteredRows = useMemo(
    () => filterByChantierOuvrier(rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedChantiers, selectedOuvriers]
  );

  const filteredRows = useMemo(() => {
    let data = [...baseFilteredRows];

    switch (kpiFilter) {
      case 'PAIE':
        data = data.filter((row) => Number(row.paie || 0) > 0);
        break;
      case 'JOURS':
        data = data.filter((row) => Number(row.jours || 0) > 0);
        break;
      case 'HEURES':
        data = data.filter((row) => Number(row.heures || 0) > 0);
        break;
      case 'ANOMALY':
        data = data.filter((row) => Number(row.taux_min || 0) !== Number(row.taux_max || 0));
        break;
      default:
        break;
    }

    return data;
  }, [baseFilteredRows, kpiFilter]);

  const kpis = useMemo(() => {
    const totalOuvriers = new Set(baseFilteredRows.map((row) => row.id_ouvrier)).size;
    const totalPaie = baseFilteredRows.reduce((sum, row) => sum + Number(row.paie || 0), 0);
    const totalJours = baseFilteredRows.reduce((sum, row) => sum + Number(row.jours || 0), 0);
    const totalHeures = baseFilteredRows.reduce((sum, row) => sum + Number(row.heures || 0), 0);

    const avecPaie = baseFilteredRows.filter((row) => Number(row.paie || 0) > 0).length;
    const avecJours = baseFilteredRows.filter((row) => Number(row.jours || 0) > 0).length;
    const avecHeures = baseFilteredRows.filter((row) => Number(row.heures || 0) > 0).length;
    const anomalies = baseFilteredRows.filter(
      (row) => Number(row.taux_min || 0) !== Number(row.taux_max || 0)
    ).length;

    return {
      totalOuvriers,
      totalPaie: Number(totalPaie.toFixed(2)),
      totalJours: Number(totalJours.toFixed(2)),
      totalHeures: Number(totalHeures.toFixed(2)),
      avecPaie,
      avecJours,
      avecHeures,
      anomalies
    };
  }, [baseFilteredRows]);

  const anomalyPreviewRows = useMemo(
    () => baseFilteredRows.filter((row) => Number(row.taux_min || 0) !== Number(row.taux_max || 0)),
    [baseFilteredRows]
  );

  const handleFixAnomalies = async () => {
    try {
      setFixingAnomalies(true);
      const res = await axios.post('/rapport/paie-mois/fix-anomalies', { start: startDate, end: endDate, mode });
      setFixResult(res.data || null);
      fetchData();
    } catch (err) {
      console.error('❌ Erreur correction anomalies :', err);
      showAlert('danger', err?.response?.data?.message || '❌ Erreur lors de la correction des anomalies');
    } finally {
      setFixingAnomalies(false);
    }
  };

  const handleOpenDoublonsModal = async () => {
    setShowDoublonsModal(true);
    setCleanResult(null);
    setDoublonsPreview(null);

    try {
      setLoadingDoublons(true);
      const res = await axios.get(`/rapport/paie-mois/doublons-preview?start=${startDate}&end=${endDate}`);
      setDoublonsPreview(res.data || null);
    } catch (err) {
      console.error('❌ Erreur preview doublons pointages :', err);
      showAlert('danger', err?.response?.data?.message || '❌ Erreur lors de la détection des doublons');
      setShowDoublonsModal(false);
    } finally {
      setLoadingDoublons(false);
    }
  };

  const handleCleanDoublons = async () => {
    try {
      setCleaningDoublons(true);
      const res = await axios.post('/rapport/paie-mois/doublons-clean', { start: startDate, end: endDate });
      setCleanResult(res.data || null);
      fetchData();
    } catch (err) {
      console.error('❌ Erreur nettoyage doublons pointages :', err);
      showAlert('danger', err?.response?.data?.message || '❌ Erreur lors du nettoyage des doublons');
    } finally {
      setCleaningDoublons(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      const otherMode = mode === 'final' ? 'doublant' : 'final';
      const res = await axios.get(`/rapport/paie-mois?start=${startDate}&end=${endDate}&mode=${otherMode}`);
      const otherRows = filterByChantierOuvrier(Array.isArray(res.data?.rows) ? res.data.rows : []);

      const finalRows = mode === 'final' ? baseFilteredRows : otherRows;
      const doublantRows = mode === 'doublant' ? baseFilteredRows : otherRows;
      const anomalyRows = finalRows.filter((row) => Number(row.taux_min || 0) !== Number(row.taux_max || 0));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, buildExcelSheet(finalRows, 'final'), 'Final');
      XLSX.utils.book_append_sheet(wb, buildExcelSheet(doublantRows, 'doublant'), 'Doublant');
      XLSX.utils.book_append_sheet(wb, buildExcelSheet(anomalyRows, 'final'), 'Anomalies');
      XLSX.writeFile(wb, `paie_mois_${startDate}_${endDate}.xlsx`);
    } catch (err) {
      console.error('❌ Erreur export Excel :', err);
      showAlert('danger', err?.response?.data?.message || '❌ Erreur lors de l’export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcelParChantier = async () => {
    if (!filteredRows.length) {
      showAlert('warning', '⚠️ Aucune donnée à exporter par chantier');
      return;
    }

    const grouped = filteredRows.reduce((acc, row) => {
      const chantier = row.dernier_chantier || 'Sans chantier';
      if (!acc[chantier]) acc[chantier] = [];
      acc[chantier].push(row);
      return acc;
    }, {});

    const chantierNames = Object.keys(grouped);

    if (!chantierNames.length) {
      showAlert('warning', '⚠️ Aucun chantier trouvé pour l’export');
      return;
    }

    try {
      setExportingParChantier(true);

      // ⚠️ Un .xlsx par chantier téléchargé séparément se fait bloquer par le navigateur
      // au-delà de quelques fichiers (protection anti-téléchargements multiples).
      // On regroupe donc tout dans un seul .zip = un seul téléchargement.
      const zip = new JSZip();

      chantierNames.forEach((chantierName) => {
        const chantierRows = grouped[chantierName];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, buildExcelSheet(chantierRows, mode), 'PaieMois');

        const safeChantier = sanitizeFileName(chantierName);
        const fileName = `paie_mois_${safeChantier}_${startDate}_${endDate}.xlsx`;
        const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

        zip.file(fileName, buffer);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `paie_mois_par_chantier_${startDate}_${endDate}.zip`);

      showAlert(
        'success',
        `✅ ${chantierNames.length} fichier(s) Excel regroupés dans un .zip téléchargé`
      );
    } catch (err) {
      console.error('❌ Erreur export Excel par chantier :', err);
      showAlert('danger', "❌ Erreur lors de l'export par chantier");
    } finally {
      setExportingParChantier(false);
    }
  };

  const columns = [
    {
      dataField: 'id_ouvrier',
      text: <><FaIdCard /> ID Ouvrier</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 ID...' })
    },
    {
      dataField: 'ouvrier',
      text: <><FaHardHat /> Ouvrier</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Ouvrier...' })
    },
    {
      dataField: 'cin',
      text: <><FaIdCard /> CIN</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 CIN...' })
    },
    {
      dataField: 'taux',
      text: <><FaCoins /> Taux</>,
      sort: true,
      formatter: (cell, row) => {
        const isAnomaly = Number(row.taux_min || 0) !== Number(row.taux_max || 0);

        return (
          <div title={`Min: ${dh(row.taux_min)} | Max: ${dh(row.taux_max)}`}>
            <div style={{ fontWeight: 700 }}>
              {dh(cell)}
            </div>

            {isAnomaly && (
              <div style={{ color: '#dc3545', fontSize: 12, fontWeight: 600 }}>
                ⚠️ Taux variable
              </div>
            )}
          </div>
        );
      }
    },
    {
      dataField: 'taux_min',
      text: 'Taux_Min',
      sort: true,
      formatter: (cell, row) => {
        const isAnomaly = Number(row.taux_min || 0) !== Number(row.taux_max || 0);
        return (
          <span style={{ color: isAnomaly ? '#dc3545' : 'inherit', fontWeight: isAnomaly ? 700 : 500 }}>
            {dh(cell)}
          </span>
        );
      }
    },
    {
      dataField: 'taux_max',
      text: 'Taux_Max',
      sort: true,
      formatter: (cell, row) => {
        const isAnomaly = Number(row.taux_min || 0) !== Number(row.taux_max || 0);
        return (
          <span style={{ color: isAnomaly ? '#dc3545' : 'inherit', fontWeight: isAnomaly ? 700 : 500 }}>
            {dh(cell)}
          </span>
        );
      }
    },
    {
      dataField: 'qualification',
      text: <><FaGraduationCap /> Qualification</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Qualification...' })
    },
    {
      dataField: 'matricule_sage',
      text: <><FaIdCard /> Matricule Sage</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Matricule...' }),
      formatter: (cell) => cell || '—'
    },
    {
      dataField: 'societe',
      text: <><FaBuilding /> Société</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Société...' }),
      formatter: (cell) => cell || '—'
    },
    {
      dataField: 'rib',
      text: <><FaUniversity /> RIB</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 RIB...' }),
      formatter: (cell) => cell || '—'
    },
    {
      dataField: 'dernier_chantier',
      text: <><FaBuilding /> {mode === 'doublant' ? 'Chantier' : 'Dernier chantier'}</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Chantier...' }),
      formatter: (cell) => cell || '—'
    },
    {
      dataField: 'jours',
      text: <><FaCalendarAlt /> Jours</>,
      sort: true,
      formatter: (cell) => Number(cell || 0)
    },
    {
      dataField: 'heures',
      text: <><FaClock /> Heures</>,
      sort: true,
      formatter: (cell) => Number(cell || 0)
    },
    {
      dataField: 'paie',
      text: <><FaMoneyBillWave /> Paie</>,
      sort: true,
      formatter: (cell) => (
        <span style={{ fontWeight: 700, color: '#198754' }}>
          {dh(cell)}
        </span>
      )
    }
  ];

  const paginationOptions = paginationFactory({
    sizePerPage: 20,
    sizePerPageList: [
      { text: '20', value: 20 },
      { text: '50', value: 50 },
      { text: '100', value: 100 },
      { text: '200', value: 200 },
      { text: 'TOUT', value: filteredRows.length || 1 }
    ],
    showTotal: true
  });

  return (
    <div className="container mt-4 mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h2 className="d-flex align-items-center gap-2 mb-1" style={{ color: '#6d4e36', fontWeight: 800 }}>
            <FaMoneyBillWave /> Paie Mois
          </h2>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Badge bg="light" text="dark" style={{ border: '1px solid #d8c8b8', padding: '10px 12px' }}>
            👤 {user?.login || user?.name || 'Utilisateur'}
          </Badge>
          <Badge bg="dark" style={{ padding: '10px 12px' }}>
            📅 {startDate} → {endDate}
          </Badge>
        </div>
      </div>

      {alertMsg.text ? (
        <Alert variant={alertMsg.type} className="mt-3">
          {alertMsg.text}
        </Alert>
      ) : null}

      {kpis.anomalies > 0 && !loading && (
        <Alert variant="danger" className="mt-3" style={{ border: '1px solid #dc3545' }}>
          <strong>⚠️ Attention :</strong> {kpis.anomalies} ouvrier(s) ont des <strong>taux variables</strong> sur la période sélectionnée.
          Vérifie les pointages avant validation finale de la paie.
        </Alert>
      )}

      <Card
        className="mb-3"
        style={{
          borderRadius: '16px',
          border: '1px solid rgba(109,78,54,0.18)',
          boxShadow: '0 8px 24px rgba(109,78,54,0.08)',
          background: '#fffdf8'
        }}
      >
        <Card.Body>
          <Row className="g-3">
            <Col md={2}>
              <Form.Label className="fw-bold"><FaCalendarAlt className="me-2" />Date Début</Form.Label>
              <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Col>

            <Col md={2}>
              <Form.Label className="fw-bold"><FaCalendarAlt className="me-2" />Date Fin</Form.Label>
              <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold"><FaBuilding className="me-2" />Chantiers</Form.Label>
              <Select
                isMulti
                isClearable
                isSearchable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="Filtrer par chantier..."
                options={chantierOptions}
                value={selectedChantiers}
                onChange={(val) => setSelectedChantiers(val || [])}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold"><FaHardHat className="me-2" />Ouvriers</Form.Label>
              <Select
                isMulti
                isClearable
                isSearchable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="Filtrer par ouvrier..."
                options={ouvrierOptions}
                value={selectedOuvriers}
                onChange={(val) => setSelectedOuvriers(val || [])}
              />
            </Col>

            <Col md={12} className="d-flex align-items-end">
              <div className="d-flex gap-2 flex-wrap w-100 align-items-center">
                <ButtonGroup>
                  <Button
                    variant={mode === 'final' ? 'dark' : 'outline-dark'}
                    onClick={() => setMode('final')}
                    disabled={loading}
                  >
                    FINAL
                  </Button>
                  <Button
                    variant={mode === 'doublant' ? 'dark' : 'outline-dark'}
                    onClick={() => setMode('doublant')}
                    disabled={loading}
                  >
                    DOUBLANT
                  </Button>
                </ButtonGroup>

                <Button
                  variant="primary"
                  onClick={handleExportExcel}
                  disabled={loading || exporting || filteredRows.length === 0}
                >
                  {exporting ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaFileExport className="me-2" />
                  )}
                  Export Excel
                </Button>

                <Button
                  variant="success"
                  onClick={handleExportExcelParChantier}
                  disabled={loading || exportingParChantier || filteredRows.length === 0}
                >
                  {exportingParChantier ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaFileExport className="me-2" />
                  )}
                  Excel par chantier
                </Button>

                <Button variant="outline-secondary" onClick={resetAllFilters}>
                  <FaBroom className="me-2" />
                  Reset filtres
                </Button>

                {canFixAnomalies && (
                  <Button variant="outline-danger" onClick={handleOpenDoublonsModal} disabled={loading}>
                    <FaClone className="me-2" />
                    Doublons pointages
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <KpiCard
            title="Total ouvriers"
            value={kpis.totalOuvriers}
            sub="Période et filtres actifs"
            color="#6d4e36"
            bg="#f8f2ea"
            icon={FaUsers}
            onClick={() => setKpiFilter('ALL')}
            active={kpiFilter === 'ALL'}
          />
        </Col>

        <Col md={3}>
          <KpiCard
            title="Total paie"
            value={dh(kpis.totalPaie)}
            sub={`${kpis.avecPaie} ligne(s) avec paie`}
            color="#198754"
            bg="#eefbf3"
            icon={FaMoneyBillWave}
            onClick={() => setKpiFilter('PAIE')}
            active={kpiFilter === 'PAIE'}
          />
        </Col>

        <Col md={2}>
          <KpiCard
            title="Total jours"
            value={kpis.totalJours}
            sub={`${kpis.avecJours} ligne(s) avec jours`}
            color="#0d6efd"
            bg="#eef7ff"
            icon={FaCalendarAlt}
            onClick={() => setKpiFilter('JOURS')}
            active={kpiFilter === 'JOURS'}
          />
        </Col>

        <Col md={2}>
          <KpiCard
            title="Total heures"
            value={kpis.totalHeures}
            sub={`${kpis.avecHeures} ligne(s) avec heures`}
            color="#3751d1"
            bg="#eef3ff"
            icon={FaClock}
            onClick={() => setKpiFilter('HEURES')}
            active={kpiFilter === 'HEURES'}
          />
        </Col>

        <Col md={2}>
          <KpiCard
            title="Anomalies"
            value={kpis.anomalies}
            sub="Taux différents détectés"
            color="#dc3545"
            bg="#fff5f5"
            icon={FaExclamationTriangle}
            onClick={() => {
              setKpiFilter('ANOMALY');
              if (kpis.anomalies > 0) {
                setFixResult(null);
                setShowAnomalyModal(true);
              }
            }}
            active={kpiFilter === 'ANOMALY'}
            alert={kpis.anomalies > 0}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <KpiCard
            title="Résumé backend"
            value={resume.totalOuvriers}
            sub="Ouvriers"
            color="#6d4e36"
            icon={FaListAlt}
          />
        </Col>

        <Col md={9}>
          <Card
            style={{
              borderRadius: '16px',
              border: '1px solid rgba(109,78,54,0.18)',
              boxShadow: '0 8px 24px rgba(109,78,54,0.08)',
              background: '#fff'
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <FaFilter />
                  <strong style={{ color: '#6d4e36' }}>
                    KPI actif : {kpiFilter === 'ALL' ? 'Toutes les lignes' : kpiFilter}
                  </strong>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  {selectedChantiers.length > 0 && (
                    <Badge bg="info" text="dark" style={{ padding: '8px 12px' }}>
                      Chantiers : {selectedChantiers.length}
                    </Badge>
                  )}

                  {selectedOuvriers.length > 0 && (
                    <Badge bg="warning" text="dark" style={{ padding: '8px 12px' }}>
                      Ouvriers : {selectedOuvriers.length}
                    </Badge>
                  )}

                  <Badge bg="secondary" style={{ padding: '8px 12px' }}>
                    Résultats : {filteredRows.length}
                  </Badge>
                </div>
              </div>

              <div className="text-muted small">
                💡 La paie est calculée ligne par ligne à partir du <strong>salaire du pointage</strong>, puis cumulée
                {mode === 'doublant' ? (
                  <> par <strong>ouvrier + chantier</strong> (un ouvrier ayant travaillé sur plusieurs chantiers apparaît sur plusieurs lignes).</>
                ) : (
                  <> par <strong>ouvrier</strong> (dernier chantier retenu).</>
                )}
                {' '}Les lignes en rouge clair signalent un <strong>changement de taux</strong> pendant la période{mode === 'doublant' ? ' sur ce chantier' : ''}.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card
        className="mb-3"
        style={{
          borderRadius: '16px',
          border: '1px solid rgba(109,78,54,0.18)',
          boxShadow: '0 8px 24px rgba(109,78,54,0.08)'
        }}
      >
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Chargement...</span>
            </div>
          ) : (
            <BootstrapTable
              keyField="row_key"
              data={filteredRows}
              columns={columns}
              pagination={paginationOptions}
              filter={filterFactory()}
              striped
              hover
              condensed
              noDataIndication="Aucune donnée de paie trouvée ❌"
              wrapperClasses="table-responsive"
              rowStyle={(row) => {
                const isAnomaly = Number(row.taux_min || 0) !== Number(row.taux_max || 0);

                if (isAnomaly) {
                  return {
                    backgroundColor: '#fff5f5',
                    borderLeft: '4px solid #dc3545'
                  };
                }

                return {};
              }}
            />
          )}
        </Card.Body>
      </Card>

      <Modal show={showAnomalyModal} onHide={() => setShowAnomalyModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#fdf2f2' }}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaExclamationTriangle style={{ color: '#dc3545' }} />
            Anomalies de taux — {startDate} → {endDate}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!fixResult ? (
            <>
              <p className="text-muted">
                {anomalyPreviewRows.length} ouvrier(s) ont un taux qui varie sur la période. Corriger alignera{' '}
                <strong>tous leurs pointages non payés</strong> de la période sur le <strong>taux max</strong> constaté.
              </p>

              <Alert variant="warning" className="py-2 small">
                <FaLock className="me-2" />
                Les pointages déjà marqués <strong>payés</strong> (date de paiement renseignée) ne seront jamais modifiés.
              </Alert>

              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Table size="sm" bordered hover responsive>
                  <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                    <tr>
                      <th>Ouvrier</th>
                      <th>CIN</th>
                      {mode === 'doublant' && <th>Chantier</th>}
                      <th className="text-end">Taux min</th>
                      <th className="text-end">Taux max</th>
                      <th className="text-end">Pointages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomalyPreviewRows.map((row) => (
                      <tr key={row.row_key}>
                        <td>{row.ouvrier}</td>
                        <td>{row.cin}</td>
                        {mode === 'doublant' && <td>{row.dernier_chantier}</td>}
                        <td className="text-end">{dh(row.taux_min)}</td>
                        <td className="text-end fw-bold" style={{ color: '#dc3545' }}>{dh(row.taux_max)}</td>
                        <td className="text-end">{row.nb_pointages}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <>
              <Alert variant="success">
                ✅ {fixResult.totalOuvriers} ouvrier(s) corrigé(s), {fixResult.totalPointagesUpdated} pointage(s) mis à jour.
                {fixResult.totalPointagesLocked > 0 && (
                  <> ⛔ {fixResult.totalPointagesLocked} pointage(s) déjà payé(s) ignoré(s).</>
                )}
              </Alert>

              {fixResult.fixed.length > 0 && (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  <Table size="sm" bordered responsive>
                    <thead>
                      <tr>
                        <th>Ouvrier</th>
                        <th>Chantier</th>
                        <th className="text-end">Nouveau taux</th>
                        <th className="text-end">Pointages MAJ</th>
                        <th className="text-end">Ignorés (payés)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fixResult.fixed.map((f, i) => (
                        <tr key={i}>
                          <td>{f.ouvrier}</td>
                          <td>{f.chantier}</td>
                          <td className="text-end">{dh(f.taux_max)}</td>
                          <td className="text-end">{f.pointages_updated}</td>
                          <td className="text-end">{f.pointages_locked || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAnomalyModal(false)}>
            Fermer
          </Button>
          {!fixResult && canFixAnomalies && (
            <Button variant="danger" onClick={handleFixAnomalies} disabled={fixingAnomalies || anomalyPreviewRows.length === 0}>
              {fixingAnomalies ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <FaWrench className="me-2" />
              )}
              Corriger {anomalyPreviewRows.length} ouvrier(s)
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showDoublonsModal} onHide={() => setShowDoublonsModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#fdf2f2' }}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaClone style={{ color: '#dc3545' }} />
            Doublons de pointages — {startDate} → {endDate}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDoublons ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Recherche des doublons...</span>
            </div>
          ) : !cleanResult ? (
            <>
              <p className="text-muted">
                Un doublon = plusieurs pointages pour le <strong>même ouvrier</strong> à la{' '}
                <strong>même date</strong>. Le plus ancien est conservé (ou celui déjà payé, s'il y en a un),
                les autres seront <strong>définitivement supprimés</strong>.
              </p>

              <Alert variant="warning" className="py-2 small">
                <FaLock className="me-2" />
                Les pointages déjà marqués <strong>payés</strong> ne seront jamais supprimés, même en doublon.
              </Alert>

              {doublonsPreview && doublonsPreview.totalDeletable === 0 ? (
                <Alert variant="success" className="py-2 small mb-0">
                  ✅ Aucun doublon supprimable trouvé sur cette période.
                  {doublonsPreview.totalLocked > 0 && (
                    <> ⛔ {doublonsPreview.totalLocked} pointage(s) en doublon déjà payé(s) — à vérifier manuellement.</>
                  )}
                </Alert>
              ) : doublonsPreview && (
                <>
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    <Badge bg="danger" style={{ padding: '8px 12px' }}>
                      {doublonsPreview.totalGroups} groupe(s) en doublon
                    </Badge>
                    <Badge bg="dark" style={{ padding: '8px 12px' }}>
                      {doublonsPreview.totalDeletable} pointage(s) à supprimer
                    </Badge>
                    {doublonsPreview.totalLocked > 0 && (
                      <Badge bg="secondary" style={{ padding: '8px 12px' }}>
                        {doublonsPreview.totalLocked} payé(s) ignoré(s)
                      </Badge>
                    )}
                  </div>

                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    <Table size="sm" bordered hover responsive>
                      <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                        <tr>
                          <th>Ouvrier</th>
                          <th>CIN</th>
                          <th>Date</th>
                          <th>Chantier</th>
                          <th className="text-end">Jours</th>
                          <th className="text-end">Heures</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {doublonsPreview.groups.map((g) => (
                          g.pointages.map((p) => (
                            <tr key={p.id} style={p.action === 'supprime' ? { background: '#fff5f5' } : {}}>
                              <td>{g.ouvrier}</td>
                              <td>{g.cin}</td>
                              <td>{g.date}</td>
                              <td>{p.chantier}</td>
                              <td className="text-end">{p.jours}</td>
                              <td className="text-end">{p.heures}</td>
                              <td>
                                {p.action === 'conserve' && <Badge bg="success">Conservé</Badge>}
                                {p.action === 'supprime' && <Badge bg="danger">À supprimer</Badge>}
                                {p.action === 'ignore_paye' && <Badge bg="secondary"><FaLock className="me-1" />Payé</Badge>}
                              </td>
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          ) : (
            <Alert variant="success">
              ✅ {cleanResult.totalDeleted} pointage(s) en doublon supprimé(s) sur {cleanResult.totalGroups} groupe(s).
              {cleanResult.totalLocked > 0 && (
                <> ⛔ {cleanResult.totalLocked} pointage(s) déjà payé(s) ignoré(s).</>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDoublonsModal(false)}>
            Fermer
          </Button>
          {!cleanResult && doublonsPreview?.totalDeletable > 0 && (
            <Button variant="danger" onClick={handleCleanDoublons} disabled={cleaningDoublons}>
              {cleaningDoublons ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <FaTrashAlt className="me-2" />
              )}
              Supprimer {doublonsPreview.totalDeletable} pointage(s)
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaieMoisPage;